
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

// Use the hardcoded values from .env as fallbacks to ensure we always have valid credentials
const supabaseUrl = "https://exjsozlpzpbnsyzluunj.supabase.co";
const supabaseKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV4anNvemxwenBibnN5emx1dW5qIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzMyNDA5MjksImV4cCI6MjA0ODgxNjkyOX0.nwvzg44wL3Ty9hV2haB1g-40t_8EEWOed7jSD7ZiBug";

export const supabase = createClient<Database>(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  global: {
    headers: { 'x-application-name': 'logic-rules-game' },
    fetch: (url, options) => {
      const fetchOptions = {
        ...options,
        signal: options?.signal || (AbortSignal?.timeout ? AbortSignal.timeout(30000) : undefined),
      };
      return fetch(url, fetchOptions);
    },
  },
  db: {
    schema: 'public',
  },
});

// Streamlined connection check that fails gracefully
export async function checkSupabaseConnection(): Promise<boolean> {
  try {
    console.log("Checking Supabase connection...");
    const startTime = performance.now();
    
    // Try to query the games table first
    const { data: gameData, error: gameError } = await supabase
      .from('games')
      .select('id')
      .limit(1);
      
    if (!gameError && gameData) {
      const endTime = performance.now();
      console.log(`Supabase connection check successful in ${endTime - startTime}ms`);
      return true;
    }
    
    // If that fails, try another table
    const { data: emailData, error: emailError } = await supabase
      .from('emails')
      .select('id')
      .limit(1);
    
    const endTime = performance.now();
    const connected = !emailError && !!emailData;
    console.log(`Supabase connection check completed in ${endTime - startTime}ms. Result: ${connected ? 'Connected' : 'Failed'}`);
    return connected;
  } catch (e) {
    // Silently fail - don't expose DB errors to users
    console.log("Supabase connection check failed:", e);
    return false;
  }
}

// Helper function to create user if not exists - with silent error handling
export async function ensureUserExists(email: string): Promise<boolean> {
  if (!email) return false;
  
  try {
    console.log(`Ensuring user exists for email: ${email}`);
    const startTime = performance.now();
    
    // Check if user exists first in the users table
    const { data: existingUser, error: userError } = await supabase
      .from('users')
      .select('email')
      .eq('email', email)
      .maybeSingle();
    
    // If there was an error checking users or user doesn't exist
    if (userError || !existingUser) {
      console.log("User doesn't exist or error checking users table, trying emails table");
      
      // Check emails table as fallback
      const { data: existingEmail, error: emailError } = await supabase
        .from('emails')
        .select('email')
        .eq('email', email)
        .maybeSingle();
      
      // If user doesn't exist in either table, create a new user record
      if ((emailError || !existingEmail) && !userError) {
        console.log("User doesn't exist in emails table either, creating new user record");
        
        // Try users table first
        const { error: insertUserError } = await supabase
          .from('users')
          .insert([{ email: email }]);
        
        if (insertUserError) {
          console.log("Failed to insert into users table, trying emails table", insertUserError);
          
          // Try emails table if users table fails
          await supabase
            .from('emails')
            .insert([{ email: email }]);
        }
      } else if (!existingUser && existingEmail) {
        console.log("User exists in emails table but not in users table, creating user record");
        
        // If email exists but user doesn't, create user
        await supabase
          .from('users')
          .insert([{ email: email }]);
      }
    } else {
      console.log("User already exists in users table");
    }
    
    const endTime = performance.now();
    console.log(`User existence check/creation completed in ${endTime - startTime}ms`);
    return true;
  } catch (error) {
    // Log for debugging but don't expose to user
    console.log("User creation fallback: using local mode only", error);
    return false;
  }
}
